<script setup lang="ts">
import contactContent from "~/content/contact.json";

const config = useRuntimeConfig();
const siteOrigin = config.public.mainUrl as string;
const { staticUrl } = useImageUrl();

definePageMeta({
  layout: "static",
});

useSeoMeta({
  title: contactContent.seo.title,
  description: contactContent.seo.description,
});

useHead({
  link: [{ rel: "canonical", href: `${siteOrigin}/contact` }],
  bodyAttrs: { "data-page": "contact" },
});

function goToUrl(card: { external?: boolean; linkHref?: string; link?: string }) {
  const url = card.linkHref || card.link || "";
  if (card.external) {
    window.location.href = url;
  } else {
    navigateTo(url);
  }
}
</script>

<template>
  <section class="contact">
    <h1 class="white-text">{{ contactContent.title }}</h1>
    <div class="contact__wrapper">
      <div
        v-for="card in contactContent.cards"
        :key="card.title"
        class="contact__wrapper--item"
        @click="goToUrl(card)"
      >
        <img
          v-if="card.image"
          :src="staticUrl(`/Images/contact/${card.image}.jpg`)"
          :alt="card.title"
          class="image-container"
          loading="lazy"
        />
        <div class="text-container">
          <h4 class="grey-text">{{ card.title }}</h4>
          <p class="light-grey-text small-text">{{ card.body }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
// Contact page sits on a hard pink canvas — the transparent navbar would
// otherwise show its dark grey nav text against pink, which fails contrast.
// Force navbar text + icons to white only on this page. Desktop only —
// on mobile the navbar is a solid white surface so dark ink is correct.
@media (min-width: 769px) {
  body[data-page="contact"] .navbar {
    &__items--tab-wrapper {
      a,
      .icon,
      svg {
        color: var(--color-white-1) !important;
      }
      &:not(.selected-link):not(:has(.selected-link)):hover {
        background-color: rgba(255, 255, 255, 0.15);
        a,
        .icon,
        svg {
          color: var(--color-white-1) !important;
        }
      }
    }
  }
}

.contact {
  width: 90%;
  padding: 10rem 5% 15rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--color-pink-1);
  &__wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 3rem 0 10rem;
    width: 75%;
    @media (max-width: $tablet) {
      flex-direction: column;
      width: 100%;
    }
    @media (max-width: $laptop) {
      width: 100%;
    }
    &--item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      cursor: pointer;
      border-radius: 1.2rem;
      width: 25%;
      height: max-content;
      background-color: var(--color-white-1);
      padding: 1rem;
      @media (max-width: $tablet) {
        width: 80%;
        margin-bottom: 3rem;
      }
      &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        transform: translateY(-5px);
        transition: all 0.3s ease;
      }
      .image-container {
        width: 100%;
        border-radius: 1.2rem;
      }
      .text-container {
        min-height: 7rem;
        h4,
        p {
          width: 100%;
          margin: 0.5rem 0;
          text-align: center;
        }
        h4 {
          height: 3rem;
        }
      }
    }
  }
  .white-text {
    @media (max-width: $tablet) {
      text-align: center;
    }
  }
}
</style>
